import React, { useState, useRef, useEffect } from 'react';
import { Plus, MapPin, X, Loader2, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { createFacility } from '../../../../services/staffingService';

function AddressAutocomplete({ onPlaceSelected }) {
  const inputRef = useRef(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    if (!window.google?.maps?.places) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      fields: ['formatted_address', 'address_components', 'geometry'],
      componentRestrictions: { country: 'us' },
    });
    const listener = ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      const comps = place.address_components || [];
      const data = {
        address: place.formatted_address,
        latitude: place.geometry.location.lat(),
        longitude: place.geometry.location.lng(),
        city: '', state: '', zipcode: '',
      };
      comps.forEach(c => {
        if (c.types.includes('locality')) data.city = c.long_name;
        if (c.types.includes('administrative_area_level_1')) data.state = c.short_name;
        if (c.types.includes('postal_code')) data.zipcode = c.long_name;
      });
      setInputValue(place.formatted_address);
      onPlaceSelected(data);
    });
    return () => window.google.maps.event.removeListener(listener);
  }, [onPlaceSelected]);

  return (
    <div className="relative">
      <MapPin size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={e => setInputValue(e.target.value)}
        placeholder="Search address…"
        className="w-full rounded-lg border border-gray-200 pl-7 pr-3 py-2 text-xs focus:outline-none focus:border-[#404880]"
      />
    </div>
  );
}

/**
 * QuickCreateFacilityPopover — small inline popover for creating a minimal facility
 * from inside NewWorkOrderSlideOver. 3 fields only: Name, Address, City/State.
 *
 * Props:
 *   customerCompanyId
 *   onCreated(facility) — called after successful creation so parent can add to list + auto-select
 *   onCancel()
 */
const QuickCreateFacilityPopover = ({ customerCompanyId, onCreated, onCancel }) => {
  const [name,       setName]       = useState('');
  const [address,    setAddress]    = useState('');
  const [city,       setCity]       = useState('');
  const [state,      setState]      = useState('');
  const [zipcode,    setZipcode]    = useState('');
  const [geoLocation, setGeoLocation] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [done,       setDone]       = useState(false);

  const handlePlaceSelected = (data) => {
    setAddress(data.address);
    setCity(data.city);
    setState(data.state);
    setZipcode(data.zipcode);
    setGeoLocation({ lat: data.latitude, lng: data.longitude });
  };

  const handleCreate = async () => {
    if (!name.trim()) return toast.error('Facility name is required');
    if (!address.trim()) return toast.error('Address is required');

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name',    name.trim());
      fd.append('address', address);
      fd.append('city',    city);
      fd.append('state',   state);
      fd.append('zipcode', zipcode);
      fd.append('status',  'active');
      if (geoLocation) {
        fd.append('geoLocation[lat]', geoLocation.lat);
        fd.append('geoLocation[lng]', geoLocation.lng);
      }
      const resp = await createFacility(customerCompanyId, fd);
      setDone(true);
      setTimeout(() => onCreated(resp.data.facility), 600);
    } catch {
      toast.error('Failed to create facility');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 rounded-xl border border-[#404880]/20 bg-[#404880]/5 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-[#404880]">Quick-add facility</p>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X size={13} />
        </button>
      </div>

      {done ? (
        <div className="flex items-center gap-2 text-xs text-emerald-700">
          <Check size={13} /> Facility created — selecting it now…
        </div>
      ) : (
        <>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Facility Name <span className="text-red-500">*</span></label>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Downtown Office"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:border-[#404880]"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Address <span className="text-red-500">*</span></label>
            <AddressAutocomplete onPlaceSelected={handlePlaceSelected} />
            {city && (
              <p className="mt-0.5 text-[10px] text-emerald-600 flex items-center gap-1">
                <MapPin size={9} /> {city}, {state} {zipcode}
              </p>
            )}
          </div>

          <p className="text-[10px] text-gray-400">Security posts, contacts and documents can be added later from the Facilities page.</p>

          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              disabled={saving}
              className="flex-1 flex items-center justify-center gap-1 rounded-lg bg-[#404880] py-2 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              {saving ? 'Creating…' : 'Create & Select'}
            </button>
            <button
              onClick={onCancel}
              className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-500 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default QuickCreateFacilityPopover;
